import { useEffect, useMemo, useState } from 'react';
import { addDays, format } from 'date-fns';
import { Plus, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useCalendarModalStore } from '@/stores/calendarModalStore';
import { useCalendarStore } from '@/stores/calendarStore';
import { useCalendarActionsStore } from '@/stores/calendarActionsStore';
import { useCalendarViewStore } from '@/stores/calendarViewStore';
import type { CalendarSendUpdatesMode, CalendarListEntry } from '@/types/calendar';
import { getTodayDateString, parseDateOnly } from '@/lib/dateOnly';

type DraftAttendee = {
  email: string;
  displayName?: string;
};

type RepeatFrequency = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

interface RepeatState {
  frequency: RepeatFrequency;
  interval: number;
  weekdays: number[];
  endMode: 'never' | 'count' | 'date';
  count?: number;
  until?: string | null;
}

const WEEKDAY_OPTIONS = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 0 },
];

const DEFAULT_REPEAT_STATE: RepeatState = {
  frequency: 'none',
  interval: 1,
  weekdays: [],
  endMode: 'never',
  count: 1,
  until: null,
};

const SEND_OPTIONS: Array<{ label: string; value: CalendarSendUpdatesMode }> = [
  { label: 'Invite everyone', value: 'all' },
  { label: 'Invite external guests', value: 'externalOnly' },
  { label: 'Don’t email attendees', value: 'none' },
];

function getDefaultFocusDate(date: string | null) {
  if (date) {
    return date;
  }
  return getTodayDateString();
}

function normalizeAttendeeInput(input: string): DraftAttendee | null {
  if (!input.trim()) {
    return null;
  }
  const bracketMatch = input.match(/^(.*)<(.+@.+)>$/);
  if (bracketMatch) {
    return {
      displayName: bracketMatch[1].trim(),
      email: bracketMatch[2].trim().toLowerCase(),
    };
  }
  if (input.includes('@')) {
    return { email: input.trim().toLowerCase() };
  }
  return null;
}

function buildDateTime(date: string, time: string) {
  const normalizedTime = time.length === 5 ? `${time}:00` : time;
  return `${date}T${normalizedTime}`;
}

function buildRecurrenceRule(state: RepeatState, startDate: string): string | null {
  if (state.frequency === 'none') {
    return null;
  }
  const parts = [`FREQ=${state.frequency.toUpperCase()}`];
  if (state.interval > 1) {
    parts.push(`INTERVAL=${state.interval}`);
  }
  if (state.frequency === 'weekly' && state.weekdays.length) {
    const map = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
    const byDay = state.weekdays
      .map((day) => map[day % 7])
      .filter(Boolean)
      .join(',');
    if (byDay) {
      parts.push(`BYDAY=${byDay}`);
    }
  }
  if (state.frequency === 'monthly') {
    const parsed = parseDateOnly(startDate);
    parts.push(`BYMONTHDAY=${parsed.getDate()}`);
  }
  if (state.frequency === 'yearly') {
    const parsed = parseDateOnly(startDate);
    parts.push(`BYMONTH=${parsed.getMonth() + 1}`);
    parts.push(`BYMONTHDAY=${parsed.getDate()}`);
  }
  if (state.endMode === 'count' && state.count && state.count > 0) {
    parts.push(`COUNT=${state.count}`);
  } else if (state.endMode === 'date' && state.until) {
    const untilValue = state.until.replace(/-/g, '');
    parts.push(`UNTIL=${untilValue}T000000Z`);
  }
  return `RRULE:${parts.join(';')}`;
}

function parseRRule(rule: string): RepeatState {
  if (!rule || !rule.startsWith('RRULE:')) {
    return { ...DEFAULT_REPEAT_STATE };
  }
  const body = rule.replace(/^RRULE:/, '');
  const parts = body.split(';').reduce<Record<string, string>>((acc, part) => {
    const [key, value] = part.split('=');
    if (key && value) {
      acc[key.toUpperCase()] = value;
    }
    return acc;
  }, {});
  const weekdays = parts.BYDAY
    ? parts.BYDAY.split(',').map((day) => ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'].indexOf(day)).filter((value) => value >= 0)
    : [];
  const until = parts.UNTIL ? `${parts.UNTIL.slice(0, 4)}-${parts.UNTIL.slice(4, 6)}-${parts.UNTIL.slice(6, 8)}` : null;
  const endMode = parts.COUNT ? 'count' : parts.UNTIL ? 'date' : 'never';
  return {
    frequency: (parts.FREQ?.toLowerCase() as RepeatFrequency) ?? 'none',
    interval: parts.INTERVAL ? Number(parts.INTERVAL) || 1 : 1,
    weekdays,
    endMode,
    count: parts.COUNT ? Number(parts.COUNT) : 1,
    until,
  } satisfies RepeatState;
}

function formatRepeatSummary(state: RepeatState) {
  if (state.frequency === 'none') {
    return 'Does not repeat';
  }
  const base = `Repeats ${state.frequency} · every ${state.interval}`;
  if (state.endMode === 'count' && state.count) {
    return `${base} · ${state.count} times`;
  }
  if (state.endMode === 'date' && state.until) {
    return `${base} · until ${format(parseDateOnly(state.until), 'MMM d, yyyy')}`;
  }
  return base;
}

export function CalendarModalHost() {
  const createDialogOpen = useCalendarModalStore((state) => state.createDialogOpen);
  const closeDialog = useCalendarModalStore((state) => state.closeCreateDialog);
  const editingEventId = useCalendarModalStore((state) => state.editingEventId);
  const draftCalendarUid = useCalendarModalStore((state) => state.draftCalendarUid);
  const draftEventData = useCalendarModalStore((state) => state.draftEventData);
  const setDraftCalendar = useCalendarModalStore((state) => state.setDraftCalendar);
  const calendarOrder = useCalendarStore((state) => state.calendarOrder);
  const calendars = useCalendarStore((state) => state.calendars);
  const visibleCalendars = useCalendarStore((state) =>
    state.useDefaultVisibleCalendars ? state.defaultCalendarUids : state.visibleCalendarUids
  );
  const defaultCreateCalendarUid = useCalendarStore((state) => state.defaultCreateCalendarUid);
  const editingEvent = useCalendarStore((state) => (editingEventId ? state.eventsById[editingEventId] : null));
  const timezone = useCalendarStore((state) => state.timezone) ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const focusDate = useCalendarViewStore((state) => state.focusDate);
  const setFocusDate = useCalendarViewStore((state) => state.setFocusDate);
  const openEventDetail = useCalendarViewStore((state) => state.openEventDetail);
  const createEvent = useCalendarActionsStore((state) => state.createEvent);
  const updateEvent = useCalendarActionsStore((state) => state.updateEvent);
  const deleteEvent = useCalendarActionsStore((state) => state.deleteEvent);
  const creatingEvent = useCalendarActionsStore((state) => state.creatingEvent);
  const updatingEventId = useCalendarActionsStore((state) => state.updatingEventId);
  const deletingEventId = useCalendarActionsStore((state) => state.deletingEventId);
  const mutationError = useCalendarActionsStore((state) => state.mutationError);

  const fallbackCalendarUid = useMemo(() => {
    // Priority: draft > user default > first visible > first available
    if (draftCalendarUid) return draftCalendarUid;
    if (defaultCreateCalendarUid && calendars[defaultCreateCalendarUid]?.canEdit === true) {
      return defaultCreateCalendarUid;
    }
    return visibleCalendars[0] ?? calendarOrder[0] ?? null;
  }, [calendarOrder, calendars, draftCalendarUid, defaultCreateCalendarUid, visibleCalendars]);

  const [title, setTitle] = useState('');
  const [calendarUid, setCalendarUid] = useState<string | null>(fallbackCalendarUid);
  const [allDay, setAllDay] = useState(false);
  const [startDate, setStartDate] = useState(getDefaultFocusDate(focusDate));
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState(getDefaultFocusDate(focusDate));
  const [endTime, setEndTime] = useState('10:00');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [attendees, setAttendees] = useState<DraftAttendee[]>([]);
  const [attendeeInput, setAttendeeInput] = useState('');
  const [sendUpdates, setSendUpdates] = useState<CalendarSendUpdatesMode>('all');
  const [useDefaultReminders, setUseDefaultReminders] = useState(true);
  const [reminderMinutes, setReminderMinutes] = useState(30);
  const [repeatState, setRepeatState] = useState<RepeatState>({ ...DEFAULT_REPEAT_STATE });
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!createDialogOpen) {
      return;
    }
    if (editingEvent) {
      setCalendarUid(editingEvent.calendarUid ?? fallbackCalendarUid);
      setDraftCalendar(editingEvent.calendarUid ?? null);
      setTitle(editingEvent.summary ?? '');
      setAllDay(Boolean(editingEvent.allDay));
      const derivedStart = editingEvent.start?.date ?? editingEvent.start?.dateTime?.slice(0, 10) ?? getDefaultFocusDate(focusDate);
      const derivedEnd = editingEvent.end?.date ?? editingEvent.end?.dateTime?.slice(0, 10) ?? derivedStart;
      setStartDate(derivedStart);
      setEndDate(
        editingEvent.allDay && derivedEnd
          ? format(addDays(parseDateOnly(derivedEnd), -1), 'yyyy-MM-dd')
          : derivedEnd
      );
      setStartTime(editingEvent.start?.dateTime ? format(new Date(editingEvent.start.dateTime), 'HH:mm') : '09:00');
      setEndTime(editingEvent.end?.dateTime ? format(new Date(editingEvent.end.dateTime), 'HH:mm') : '10:00');
      setLocation(editingEvent.location ?? '');
      setDescription(editingEvent.description ?? '');
      setAttendees(
        (editingEvent.attendees ?? [])
          .filter((attendee) => Boolean(attendee.email))
          .map((attendee) => ({ email: attendee.email!, displayName: attendee.displayName ?? undefined }))
      );
      setSendUpdates('all');
      setUseDefaultReminders(editingEvent.reminders?.useDefault ?? true);
      setReminderMinutes(editingEvent.reminders?.overrides?.[0]?.minutes ?? 30);
      setRepeatState(editingEvent.recurrence?.length ? parseRRule(editingEvent.recurrence[0]) : { ...DEFAULT_REPEAT_STATE });
    } else {
      const presetDate = draftEventData?.date ?? getDefaultFocusDate(focusDate);
      setCalendarUid(fallbackCalendarUid);
      setDraftCalendar(fallbackCalendarUid);
      setTitle(draftEventData?.summary ?? '');
      setAllDay(draftEventData?.allDay ?? false);
      setStartDate(presetDate);
      setEndDate(presetDate);
      // Convert minutes to time string if provided
      if (draftEventData?.startMinutes != null) {
        const hours = Math.floor(draftEventData.startMinutes / 60);
        const mins = draftEventData.startMinutes % 60;
        setStartTime(`${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`);
      } else {
        setStartTime('09:00');
      }
      if (draftEventData?.endMinutes != null) {
        const hours = Math.floor(draftEventData.endMinutes / 60);
        const mins = draftEventData.endMinutes % 60;
        setEndTime(`${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`);
      } else {
        setEndTime('10:00');
      }
      setLocation('');
      setDescription('');
      setAttendees([]);
      setSendUpdates('all');
      setUseDefaultReminders(true);
      setReminderMinutes(30);
      setRepeatState({ ...DEFAULT_REPEAT_STATE });
    }
    setAttendeeInput('');
    setFormError(null);
  }, [createDialogOpen, editingEvent, draftEventData, fallbackCalendarUid, focusDate, setDraftCalendar]);

  const calendarOptions = useMemo(() => {
    return calendarOrder
      .map((uid) => calendars[uid])
      .filter(Boolean) as CalendarListEntry[];
  }, [calendarOrder, calendars]);

  const isSubmitting = editingEventId ? updatingEventId === editingEventId : creatingEvent;
  const isDeleting = editingEventId ? deletingEventId === editingEventId : false;

  const handleAddAttendee = () => {
    const normalized = normalizeAttendeeInput(attendeeInput);
    if (!normalized) {
      return;
    }
    setAttendees((prev) => {
      if (prev.some((item) => item.email.toLowerCase() === normalized.email.toLowerCase())) {
        return prev;
      }
      return [...prev, normalized];
    });
    setAttendeeInput('');
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!calendarUid) {
      setFormError('Select a calendar for this event.');
      return;
    }
    if (!startDate) {
      setFormError('Choose a start date');
      return;
    }
    if (!allDay && (!startTime || !endTime)) {
      setFormError('Provide start and end times');
      return;
    }
    const normalizedEndDate = endDate < startDate ? startDate : endDate;
    if (!allDay) {
      const startValue = new Date(`${startDate}T${startTime}`);
      const endValue = new Date(`${normalizedEndDate}T${endTime}`);
      if (endValue <= startValue) {
        setFormError('End time must be after the start time');
        return;
      }
    }
    const exclusiveEndDate = allDay ? format(addDays(parseDateOnly(normalizedEndDate), 1), 'yyyy-MM-dd') : normalizedEndDate;
    const recurrenceRule = buildRecurrenceRule(repeatState, startDate);
    const payload = {
      summary: title || 'Untitled event',
      location,
      description,
      allDay,
      start: allDay
        ? { date: startDate, dateTime: null, timeZone: timezone }
        : { date: null, dateTime: buildDateTime(startDate, startTime), timeZone: timezone },
      end: allDay
        ? { date: exclusiveEndDate, dateTime: null, timeZone: timezone }
        : { date: null, dateTime: buildDateTime(normalizedEndDate, endTime), timeZone: timezone },
      attendees: attendees.map((attendee) => ({
        email: attendee.email,
        displayName: attendee.displayName ?? null,
      })),
      reminders: useDefaultReminders
        ? { useDefault: true, overrides: [] }
        : { useDefault: false, overrides: [{ method: 'popup', minutes: reminderMinutes }] },
      recurrence: recurrenceRule ? [recurrenceRule] : [],
    };

    if (editingEventId) {
      const result = await updateEvent({ calendarUid, eventId: editingEventId, event: payload, sendUpdates });
      if (result) {
        closeDialog();
        if (result.startDate) {
          setFocusDate(result.startDate);
          openEventDetail(result.id);
        }
      }
      return;
    }

    const created = await createEvent({ calendarUid, event: payload, sendUpdates });
    if (created) {
      closeDialog();
      if (created.startDate) {
        setFocusDate(created.startDate);
        openEventDetail(created.id);
      }
    }
  };

  const handleDelete = async () => {
    if (!editingEventId || !calendarUid) {
      return;
    }
    const success = await deleteEvent({ calendarUid, eventId: editingEventId, sendUpdates: 'all' });
    if (success) {
      closeDialog();
    }
  };

  return (
    <Dialog open={createDialogOpen} onOpenChange={(open) => (open ? null : closeDialog())}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{editingEventId ? 'Edit event' : 'New event'}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-5 py-2 pr-1">
            {!calendarOptions.length ? (
              <div className="rounded-xl border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
                Connect a Google Calendar account to create events.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label>Calendar</Label>
                    <Select
                      value={calendarUid ?? ''}
                      onValueChange={(value) => {
                        setCalendarUid(value);
                        setDraftCalendar(value);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose calendar" />
                      </SelectTrigger>
                      <SelectContent>
                        {calendarOptions.map((calendar) => (
                          <SelectItem key={calendar.uid} value={calendar.uid}>
                            <span className="inline-flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: calendar.backgroundColor ?? '#94a3b8' }} />
                              {calendar.summary}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Team sync" />
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label>Start</Label>
                    <div className="flex items-center gap-2">
                      <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                      {!allDay && (
                        <Input type="time" value={startTime} onChange={(event) => setStartTime(event.target.value)} />
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>End</Label>
                    <div className="flex items-center gap-2">
                      <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                      {!allDay && (
                        <Input type="time" value={endTime} onChange={(event) => setEndTime(event.target.value)} />
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border/60 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">All day</p>
                    <p className="text-xs text-muted-foreground">Treat this event as an all-day block.</p>
                  </div>
                  <Switch checked={allDay} onCheckedChange={setAllDay} />
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label>Location</Label>
                    <Input value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Conference room" />
                  </div>
                  <div>
                    <Label>Reminders</Label>
                    <div className="flex items-center gap-3 rounded-xl border border-border/60 px-3 py-2">
                      <Switch
                        checked={!useDefaultReminders}
                        onCheckedChange={(checked) => setUseDefaultReminders(!checked)}
                      />
                      {useDefaultReminders ? (
                        <p className="text-xs text-muted-foreground">Use calendar defaults</p>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <Input
                            type="number"
                            className="w-20"
                            min={1}
                            value={reminderMinutes}
                            onChange={(event) => setReminderMinutes(Math.max(1, Number(event.target.value) || 1))}
                          />
                          <span className="text-xs text-muted-foreground">minutes before</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Attendees</Label>
                    <Select value={sendUpdates} onValueChange={(value: CalendarSendUpdatesMode) => setSendUpdates(value)}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SEND_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      value={attendeeInput}
                      onChange={(event) => setAttendeeInput(event.target.value)}
                      placeholder="person@company.com"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleAddAttendee();
                        }
                      }}
                    />
                    <Button type="button" variant="secondary" onClick={handleAddAttendee}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {attendees.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {attendees.map((attendee) => (
                        <Badge key={attendee.email} variant="secondary" className="flex items-center gap-1">
                          {attendee.displayName ?? attendee.email}
                          <button type="button" onClick={() => setAttendees((prev) => prev.filter((item) => item.email !== attendee.email))}>
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-3 rounded-xl border border-border/60 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">Repeat</p>
                      <p className="text-xs text-muted-foreground">{formatRepeatSummary(repeatState)}</p>
                    </div>
                    <Select
                      value={repeatState.frequency}
                      onValueChange={(value: RepeatFrequency) => setRepeatState((prev) => ({ ...prev, frequency: value }))}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Does not repeat</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {repeatState.frequency !== 'none' && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label>Every</Label>
                        <Input
                          type="number"
                          className="w-20"
                          min={1}
                          value={repeatState.interval}
                          onChange={(event) =>
                            setRepeatState((prev) => ({ ...prev, interval: Math.max(1, Number(event.target.value) || 1) }))
                          }
                        />
                        <span className="text-sm text-muted-foreground">{repeatState.frequency}</span>
                      </div>
                      {repeatState.frequency === 'weekly' && (
                        <div className="flex flex-wrap gap-2">
                          {WEEKDAY_OPTIONS.map((day) => {
                            const active = repeatState.weekdays.includes(day.value);
                            return (
                              <button
                                key={day.value}
                                type="button"
                                className={cn(
                                  'rounded-full px-3 py-1 text-xs font-semibold transition',
                                  active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                                )}
                                onClick={() =>
                                  setRepeatState((prev) => ({
                                    ...prev,
                                    weekdays: active
                                      ? prev.weekdays.filter((value) => value !== day.value)
                                      : [...prev.weekdays, day.value],
                                  }))
                                }
                              >
                                {day.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <label className="flex items-center gap-1 text-foreground">
                          <input
                            type="radio"
                            className="accent-primary"
                            checked={repeatState.endMode === 'never'}
                            onChange={() => setRepeatState((prev) => ({ ...prev, endMode: 'never' }))}
                          />
                          Never ends
                        </label>
                        <label className="flex items-center gap-1 text-foreground">
                          <input
                            type="radio"
                            className="accent-primary"
                            checked={repeatState.endMode === 'count'}
                            onChange={() => setRepeatState((prev) => ({ ...prev, endMode: 'count' }))}
                          />
                          After
                          <Input
                            type="number"
                            className="ml-2 w-16"
                            disabled={repeatState.endMode !== 'count'}
                            min={1}
                            value={repeatState.count ?? 1}
                            onChange={(event) =>
                              setRepeatState((prev) => ({ ...prev, count: Math.max(1, Number(event.target.value) || 1) }))
                            }
                          />
                          times
                        </label>
                        <label className="flex flex-wrap items-center gap-1 text-foreground">
                          <input
                            type="radio"
                            className="accent-primary"
                            checked={repeatState.endMode === 'date'}
                            onChange={() => setRepeatState((prev) => ({ ...prev, endMode: 'date' }))}
                          />
                          On date
                          <Input
                            type="date"
                            className="ml-2"
                            disabled={repeatState.endMode !== 'date'}
                            value={repeatState.until ?? ''}
                            onChange={(event) => setRepeatState((prev) => ({ ...prev, until: event.target.value }))}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                {formError && <p className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{formError}</p>}
                {mutationError && <p className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{mutationError}</p>}
              </div>
            )}
          </div>
        </ScrollArea>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {editingEventId && (
            <Button variant="ghost" className="text-destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Delete
            </Button>
          )}
          <div className="flex w-full justify-end gap-2 sm:w-auto">
            <Button variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || !calendarOptions.length}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
